import { Minus, Plus, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { MAX_WAIT_SEC } from '@/config/scenarioConfig'

interface WaitSecondsInputProps {
  // 停止秒数（0 = 未入力）
  value: number
  onChange: (sec: number) => void
  // true でスライダーを省いた「[時計] [−][値][+] 秒」のみの表示（一覧の行内用）
  compact?: boolean
  className?: string
}

// スライダーで直接指定できる上限（秒）。これ以上は +10 ボタンで上げる（最大 MAX_WAIT_SEC）
const SLIDER_MAX_SEC = 300

// fork_polemap_generator_v2_react の回転微調整と同じボタン様式（デスクトップ向けに小さめ）
const STEP_BTN =
  'min-w-9 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm active:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center touch-manipulation'

// 停止手順の秒数入力。fork_webapp_react の WaitSecondsInput を i18n 対応で移植したもの。
// 上段に −10 / 現在値 / +10、下段に −1 / スライダー / +1 を置く。
export default function WaitSecondsInput({
  value,
  onChange,
  compact = false,
  className = '',
}: WaitSecondsInputProps) {
  const { t } = useTranslation()
  const clamp = (sec: number) => Math.min(Math.max(sec, 0), MAX_WAIT_SEC)
  const step = (delta: number) => onChange(clamp(value + delta))
  const unit = t('scenarios.waitUnit')

  if (compact) {
    return (
      <div className={`flex items-center gap-1 text-[11px] ${className}`}>
        <Timer size={12} className="text-gray-500" />
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={value <= 0}
          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white active:bg-gray-200 disabled:opacity-40 touch-manipulation"
          aria-label="-1"
        >
          <Minus size={12} />
        </button>
        <span className="min-w-8 text-center text-xs font-bold">{value}</span>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={value >= MAX_WAIT_SEC}
          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white active:bg-gray-200 disabled:opacity-40 touch-manipulation"
          aria-label="+1"
        >
          <Plus size={12} />
        </button>
        <span className="text-gray-600">{unit}</span>
      </div>
    )
  }

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {/* 上段: ±10 秒と現在値 */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={STEP_BTN}
          onClick={() => step(-10)}
          disabled={value <= 0}
          aria-label="-10"
        >
          −10
        </button>
        <span className="flex flex-1 items-center justify-center gap-1.5 text-center text-base font-bold">
          <Timer size={16} className="text-gray-500" />
          {value} {unit}
        </span>
        <button
          type="button"
          className={STEP_BTN}
          onClick={() => step(10)}
          disabled={value >= MAX_WAIT_SEC}
          aria-label="+10"
        >
          +10
        </button>
      </div>
      {/* 下段: ±1 秒とスライダー（スライダーは SLIDER_MAX_SEC まで） */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={STEP_BTN}
          onClick={() => step(-1)}
          disabled={value <= 0}
          aria-label="-1"
        >
          <Minus size={14} />
        </button>
        <div className="relative flex h-6 flex-1 items-center">
          <input
            type="range"
            min={0}
            max={SLIDER_MAX_SEC}
            step={1}
            value={Math.min(value, SLIDER_MAX_SEC)}
            onChange={(e) => onChange(clamp(Number(e.target.value)))}
            className="relative z-10 h-full w-full min-w-0 cursor-pointer bg-transparent accent-blue-500"
            aria-label={t('scenarios.waitSec')}
          />
        </div>
        <button
          type="button"
          className={STEP_BTN}
          onClick={() => step(1)}
          disabled={value >= MAX_WAIT_SEC}
          aria-label="+1"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  )
}
